import { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  FileText,
  Home,
  Plus,
  Search,
  Settings,
  Star,
  Trash2,
  Clock,
  MoreHorizontal,
  PanelLeftClose,
  PanelLeft,
  CheckSquare,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useUIStore, useDocumentStore, useAuthStore, Document } from '../lib/store';
import { documentsApi } from '../lib/api';
import SpotifyPlayer from './SpotifyPlayer';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
  isActive?: boolean;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  depth?: number;
}

function NavItem({
  icon,
  label,
  href,
  onClick,
  isActive,
  children,
  actions,
  depth = 0,
}: NavItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const hasChildren = Boolean(children);

  const paddingStart = depth * 12;

  const content = (
    <div
      className={cn(
        'group relative flex items-center gap-2 rounded-md px-2 py-1.5 text-sm',
        'transition-colors duration-150',
        'hover:bg-secondary',
        isActive && 'bg-secondary font-medium'
      )}
      style={{ paddingInlineStart: `${paddingStart + 8}px` }}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {hasChildren && (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setExpanded(!expanded);
          }}
          className="p-0.5 rounded hover:bg-secondary-foreground/10"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flip-rtl" />
          )}
        </button>
      )}
      <span className="text-muted-foreground shrink-0">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {actions && showActions && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {actions}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {href ? (
        <Link to={href} onClick={onClick}>
          {content}
        </Link>
      ) : (
        <button onClick={onClick} className="w-full text-start">
          {content}
        </button>
      )}
      {hasChildren && expanded && (
        <div className="mt-0.5">{children}</div>
      )}
    </div>
  );
}

interface DocumentTreeItemProps {
  document: Document;
  documents: Document[];
  depth?: number;
}

function DocumentTreeItem({ document, documents, depth = 0 }: DocumentTreeItemProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { addDocument } = useDocumentStore();
  const isActive = location.pathname === `/app/document/${document.id}`;

  const childDocuments = documents.filter(
    (doc) => doc.parentId === document.id && !doc.isArchived
  );

  const handleAddChild = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await documentsApi.create({ 
        title: '', 
        parentId: document.id 
      });
      addDocument(response.document);
      navigate(`/app/document/${response.document.id}`);
    } catch {
      // Handle error silently
    }
  };

  return (
    <NavItem
      icon={document.icon ? <span className="text-base">{document.icon}</span> : <FileText className="h-4 w-4" />}
      label={document.title || t('document.untitled')}
      href={`/app/document/${document.id}`}
      isActive={isActive}
      depth={depth}
      actions={
        <button
          onClick={handleAddChild}
          className="p-1 rounded hover:bg-secondary-foreground/10"
          title={t('document.newPage')}
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
      }
    >
      {childDocuments.length > 0 &&
        childDocuments.map((child) => (
          <DocumentTreeItem
            key={child.id}
            document={child}
            documents={documents}
            depth={depth + 1}
          />
        ))}
    </NavItem>
  );
}

export default function Sidebar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const { documents, addDocument } = useDocumentStore();
  const { user } = useAuthStore();
  const isRtl = i18n.language === 'ar';

  // Filter documents
  const rootDocuments = documents.filter(
    (doc) => !doc.parentId && !doc.isArchived
  );
  const favoriteDocuments = documents.filter(
    (doc) => doc.isFavorited && !doc.isArchived
  );
  const recentDocuments = documents
    .filter((doc) => !doc.isArchived)
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const handleCreateDocument = async () => {
    try {
      const response = await documentsApi.create({ title: '' });
      addDocument(response.document);
      navigate(`/app/document/${response.document.id}`);
    } catch {
      const newDoc: Document = {
        id: crypto.randomUUID(),
        title: '',
        parentId: null,
        ownerId: user?.id || 'local',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        isArchived: false,
        isFavorited: false,
      };
      addDocument(newDoc);
      navigate(`/app/document/${newDoc.id}`);
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'sidebar',
          !sidebarOpen && 'sidebar-collapsed'
        )}
      >
        {/* Header with user */}
        <div className="flex h-12 items-center justify-between gap-2 px-3 border-b">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="h-6 w-6 rounded bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs font-bold text-white shrink-0">
              {user?.name?.charAt(0).toUpperCase() || 'N'}
            </div>
            <span className="font-medium text-sm truncate">
              {user?.name || t('sidebar.workspace')}
            </span>
          </div>
          <button
            onClick={toggleSidebar}
            className="p-1 rounded hover:bg-secondary"
            title={t('nav.home')}
          >
            <PanelLeftClose className="h-4 w-4 flip-rtl" />
          </button>
        </div>

        {/* Search */}
        <div className="p-2">
          <button className="menu-item text-muted-foreground">
            <Search className="h-4 w-4" />
            <span>{t('nav.search')}</span>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {/* Home */}
          <NavItem
            icon={<Home className="h-4 w-4" />}
            label={t('nav.home')}
            href="/app"
            isActive={location.pathname === '/app'}
          />

          {/* Favorites */}
          {favoriteDocuments.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center px-2 mb-1">
                <span className="text-xs font-medium text-muted-foreground">
                  {t('sidebar.favorites')}
                </span>
              </div>
              {favoriteDocuments.map((doc) => (
                <NavItem
                  key={doc.id}
                  icon={doc.icon ? <span>{doc.icon}</span> : <Star className="h-4 w-4 text-yellow-500" />}
                  label={doc.title || t('document.untitled')}
                  href={`/app/document/${doc.id}`}
                  isActive={location.pathname === `/app/document/${doc.id}`}
                />
              ))}
            </div>
          )}

          {/* Private pages */}
          <div className="mt-4">
            <div className="flex items-center justify-between px-2 mb-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t('sidebar.private')}
              </span>
              <button
                onClick={handleCreateDocument}
                className="p-1 rounded hover:bg-secondary"
                title={t('nav.newDocument')}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>
            {rootDocuments.map((doc) => (
              <DocumentTreeItem
                key={doc.id}
                document={doc}
                documents={documents}
              />
            ))}
            {rootDocuments.length === 0 && (
              <button
                onClick={handleCreateDocument}
                className="menu-item text-muted-foreground"
              >
                <Plus className="h-4 w-4" />
                <span>{t('document.newPage')}</span>
              </button>
            )}
          </div>

          {/* Recent */}
          {recentDocuments.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center px-2 mb-1">
                <Clock className="h-3 w-3 me-1.5 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground">
                  {t('sidebar.recent')}
                </span>
              </div>
              {recentDocuments.slice(0, 3).map((doc) => (
                <NavItem
                  key={doc.id}
                  icon={doc.icon ? <span>{doc.icon}</span> : <FileText className="h-4 w-4" />}
                  label={doc.title || t('document.untitled')}
                  href={`/app/document/${doc.id}`}
                  isActive={location.pathname === `/app/document/${doc.id}`}
                />
              ))}
            </div>
          )}
        </nav>

        {/* Footer */}
        <div className="border-t p-2">
          {/* Spotify Player */}
          <SpotifyPlayer className="mb-1" />
          
          <NavItem
            icon={<CheckSquare className="h-4 w-4" />}
            label={t('nav.tasks') || 'Tasks'}
            href="/app/tasks"
            isActive={location.pathname === '/app/tasks'}
          />
          <NavItem
            icon={<Trash2 className="h-4 w-4" />}
            label={t('sidebar.trash')}
          />
          <NavItem
            icon={<Settings className="h-4 w-4" />}
            label={t('nav.settings')}
            href="/app/settings"
            isActive={location.pathname === '/app/settings'}
          />
        </div>
      </aside>

      {/* Toggle button when collapsed */}
      {!sidebarOpen && (
        <button
          onClick={toggleSidebar}
          className="fixed top-3 z-40 p-2 rounded-md hover:bg-secondary bg-background border shadow-sm transition-all"
          style={{ insetInlineStart: '12px' }}
        >
          <PanelLeft className="h-4 w-4 flip-rtl" />
        </button>
      )}
    </>
  );
}
